import React, { ReactNode, useState } from 'react';
import { Alert } from '@/shared/ui';
import { getAddressDetails, getAddressPredictions } from '@/shared/helpers/address';
import { AdvancedAutoComplete } from '../autocomplete/adavanced-autoComplete';

interface AddressInputProps {
    uniqueIdentifier?: string;
    label?: string;
    subLabel?: string;
    boldLabel?: boolean;
    name?: string;
    placeholder?: string;
    required?: boolean;
    onInputBlur?: () => void;
    selectedAddress?: any;
    onInputChange?: (event: React.SyntheticEvent<Element, Event>) => void;
    onAddressSeleted?: (address: any) => void;
    hasDelete?: boolean;
    hasManualInput?: boolean;
    popover?: boolean;
    onPopoverClose?: () => void;
    onManualInput?(): void;
    error?: boolean;
    errorMessage?: string;
}

export const AddressInput = ({
    uniqueIdentifier = 'address-input',
    required,
    label,
    subLabel,
    selectedAddress,
    onAddressSeleted,
    hasManualInput,
    onManualInput,
    placeholder,
    onInputChange,
    error,
    errorMessage
}: AddressInputProps) => {
    const [inputValue, setInputValue] = useState(selectedAddress?.fullAddress || '');

    const prepareAddressData = (componentsArr: Array<any>, placeByTypes: any) => {
        const addressData: any = {};
        componentsArr.map(([component, config]) => {
            let value;
            if (placeByTypes.hasOwnProperty(component)) {
                value = placeByTypes[component][config['len']];
            } else {
                if (config.hasOwnProperty('options')) {
                    config.options.some((option: any) => {
                        // breaks loop after first match
                        if (placeByTypes.hasOwnProperty(option)) {
                            value = placeByTypes[option][config['len']];
                            return true;
                        }
                    });
                }
            }
            addressData[config['field']] = value;
        });
        return addressData;
    };
    const classifyPlaceByTypes = (place: any) => {
        const placeByType: any = {};
        place.map((component: any) => {
            const type = component.types[0];
            placeByType[type] = component;
        });
        return placeByType;
    };
    const getAddressFromPlace = (place: any) => {
        if (!Array.isArray(place)) {
            return {
                line1: null,
                line2: null,
                city: null,
                state: null,
                zip: null
            };
        }
        const componentForm = {
            street_number: { len: 'short_name', field: 'line1' },
            route: { len: 'long_name', field: 'line2' },
            locality: { len: 'long_name', field: 'city', options: ['postal_town', 'administrative_area_level_2'] },
            administrative_area_level_1: { len: 'short_name', field: 'state' },
            postal_code: { len: 'short_name', field: 'zip' },
            subpremise: { len: 'short_name', field: 'subpremise' }
        };
        const placeByTypes = classifyPlaceByTypes(place);
        const componentsArr = Object.entries(componentForm);
        const addressData = prepareAddressData(componentsArr, placeByTypes);
        return addressData;
    };

    const handlePlaceChange = async (option: { place_id: string }) => {
        if (option.place_id === 'MANUAL_INPUT') return onManualInput?.();

        const place = await getAddressDetails(option.place_id);
        const parsedAddress: any = getAddressFromPlace(place?.address_components);
        const processed = {
            fullAddress: `${parsedAddress.line1} ${parsedAddress.line2}, ${parsedAddress.city}, ${parsedAddress.state} ${parsedAddress.zip}`,
            addressLine1: `${parsedAddress.line1 || ''} ${parsedAddress.line2 || ''}`.trim(),
            addressLine2: '',
            city: parsedAddress.city,
            state: parsedAddress.state,
            zip: parsedAddress.zip,
            unit: parsedAddress.subpremise || '',
            verified: false,
            coordinates: {
                lat: place?.geometry?.location?.lat(),
                lng: place?.geometry?.location?.lng()
            }
        };

        onAddressSeleted?.(processed);
    };

    return (
        <>
            <AdvancedAutoComplete<{ place_id: string; title?: string; description?: string; content?: ReactNode }>
                className="address_input"
                popoverInputVariant='advanced'
                popoverTitle="Search for your address"
                keepSelectedValue
                value={inputValue}
                onInput={onInputChange}
                onChange={setInputValue}
                needDict={false}
                uniqueIdentifier={uniqueIdentifier}
                required={required}
                label={label}
                subLabel={subLabel}
                placeholder={placeholder}
                labelKey="description"
                freeContentKey="content"
                loadOptionsList={getAddressPredictions}
                onSelect={handlePlaceChange}
                error={Boolean(error && errorMessage)}
                extraOptions={
                    hasManualInput
                        ? [
                              {
                                  place_id: 'MANUAL_INPUT',
                                  content: (
                                      <div className="flex flex-col">
                                          <span>Don't see your address</span>
                                          <span className="text-primary-400">Add your address manually</span>
                                      </div>
                                  )
                              }
                          ]
                        : []
                }
            />
            {error && errorMessage && <Alert className="mt-2.5 font-dmSans lg:mt-6" label={errorMessage} />}
        </>
    );
};

export default AddressInput;
